import { useState, useEffect } from 'react';
import { Dimensions, ScaledSize } from 'react-native';
import { isWeb, breakpoints } from './platform';

export type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'wide';

interface ResponsiveState {
  width: number;
  height: number;
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWide: boolean;
}

/**
 * Hook for responsive design - returns current breakpoint and dimensions
 * Updates on window resize (web) or orientation change (native)
 *
 * SSG note: Returns mobile defaults until client mount to keep server-rendered
 * HTML stable. Without this, `Dimensions.get('window')` returns build-time
 * defaults during static export and real viewport size on the client, causing
 * hydration mismatches (React error #418) on any component that branches on
 * `isDesktop`.
 */
export function useResponsive(): ResponsiveState {
  const [mounted, setMounted] = useState(false);
  const [dimensions, setDimensions] = useState(() => Dimensions.get('window'));

  useEffect(() => {
    setMounted(true);
    setDimensions(Dimensions.get('window'));

    const handleChange = ({ window }: { window: ScaledSize }) => {
      setDimensions(window);
    };

    const subscription = Dimensions.addEventListener('change', handleChange);

    return () => {
      subscription.remove();
    };
  }, []);

  const { width, height } = dimensions;

  let breakpoint: Breakpoint = 'mobile';
  if (mounted && isWeb) {
    if (width >= breakpoints['xl']) {
      breakpoint = 'wide';
    } else if (width >= breakpoints.lg) {
      breakpoint = 'desktop';
    } else if (width >= breakpoints.md) {
      breakpoint = 'tablet';
    }
  }

  return {
    width: mounted ? width : 0,
    height: mounted ? height : 0,
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop' || breakpoint === 'wide',
    isWide: breakpoint === 'wide',
  };
}

/**
 * Returns a value based on current breakpoint
 * Similar to CSS media queries but in JS
 */
export function useBreakpointValue<T>(values: {
  mobile?: T;
  tablet?: T;
  desktop?: T;
  wide?: T;
  default: T;
}): T {
  const { breakpoint } = useResponsive();

  return values[breakpoint] ?? values.default;
}
